<template>
  <div class="order-proceeding-page">
    <div class="table-container">
      <div class="table-header">
        <h3>Order Monitoring</h3>
        <div class="header-actions">
          <!-- New Order 按钮 - 改成绿色 -->
          <el-button 
            type="success" 
            size="small"
            @click="dialogVisible = true"
            style="font-weight: 600;"
          >
            <el-icon><Plus /></el-icon>
            New Order
          </el-button>

          <!-- Search 功能 -->
          <div class="search-container">
            <span class="search-label">
              <el-icon><Search /></el-icon>
              Search
            </span>
            <el-input
              v-model="searchKeyword"
              placeholder="Order No/Customer/Vessel/Port"
              size="small"
              style="width: 180px;"
              clearable
              @keyup.enter="applySearch"
              @clear="clearSearch"
            />
            <div class="search-actions-inline">
              <el-button 
                size="small" 
                type="primary" 
                plain
                style="padding: 2px 10px; font-size: 11px;"
                @click="applySearch"
              >
                Start
              </el-button>
              <el-button 
                size="small" 
                type="danger" 
                plain
                style="padding: 2px 6px; font-size: 11px;"
                @click="clearSearch"
              >
                Clear
              </el-button>
            </div>
          </div>

          <!-- Sorting 功能 -->
          <div class="sorting-container">
            <span class="sorting-label">
              <el-icon><Sort /></el-icon>
              Sorting
            </span>
            <div class="sorting-fields">
              <div 
                v-for="(item, index) in sortFields" 
                :key="index"
                class="sorting-field-item"
              >
                <span class="sort-num">{{ index + 1 }}</span>
                <el-select 
                  v-model="item.field" 
                  placeholder="字段"
                  size="small"
                  style="width: 60px;"
                >
                  <el-option 
                    v-for="col in sortableColumns" 
                    :key="col.prop"
                    :label="col.label" 
                    :value="col.prop"
                    :disabled="isFieldUsed(item.field, index)"
                  />
                </el-select>
                <el-select 
                  v-model="item.order" 
                  placeholder="顺序"
                  size="small"
                  style="width: 45px; margin-left: 4px;"
                >
                  <el-option label="A" value="ascending" />
                  <el-option label="D" value="descending" />
                </el-select>
              </div>
            </div>
            <div class="sorting-actions-inline">
              <el-button 
                size="small" 
                type="primary" 
                plain
                style="padding: 2px 10px; font-size: 11px;"
                @click="applySort"
              >
                Start
              </el-button>
              <el-button 
                size="small" 
                type="danger" 
                plain
                style="padding: 2px 6px; font-size: 11px;"
                @click="clearSort"
              >
                Clear
              </el-button>
            </div>
          </div>

          <!-- Row Setting 按钮 - 改成绿色 -->
          <el-popover
            placement="bottom-end"
            width="auto"
            trigger="click"
          >
            <template #reference>
              <el-button size="small" type="success" plain>
                <el-icon><Setting /></el-icon>
                Row Setting
              </el-button>
            </template>
            <div class="column-setting-horizontal">
              <div 
                v-for="col in visibleColumns" 
                :key="col.prop"
                class="column-setting-item-horizontal"
              >
                <el-checkbox 
                  v-model="col.visible" 
                  :label="col.label"
                  size="small"
                />
              </div>
            </div>
          </el-popover>
        </div>
      </div>
      <el-table 
        :data="displayData" 
        border 
        style="width: 100%" 
        max-height="600"
        v-loading="loading"
        :row-style="{ height: 'auto' }"
        :cell-style="{ padding: '0px 2px' }"
        :resizable="false"
        row-key="id"
      >
        <el-table-column type="index" label="No" width="40" align="center" />
        
        <el-table-column 
          prop="orderNo" 
          label="Order No" 
          width="80"
          v-if="getColumnVisible('orderNo')"
        >
          <template #default="{ row }">
            <span class="cell-text">{{ row.orderNo }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="customer" 
          label="Customer" 
          width="100"
          v-if="getColumnVisible('customer')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.customer }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="vessel" 
          label="Vessel" 
          width="130"
          v-if="getColumnVisible('vessel')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.vessel }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="imo" 
          label="IMO" 
          width="60"
          v-if="getColumnVisible('imo')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.imo }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="type" 
          label="Type" 
          width="70"
          v-if="getColumnVisible('type')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.type }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="port" 
          label="Port" 
          width="90"
          v-if="getColumnVisible('port')"
        >
          <template #default="{ row }">
            <span class="cell-text cell-left">{{ row.port }}</span>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="etb" 
          label="ETB" 
          width="80"
          v-if="getColumnVisible('etb')"
        >
          <template #default="{ row }">
            <el-date-picker
              v-model="row.etb" 
              type="datetime"
              size="small"
              placeholder="Date Time"
              format="MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 100%"
              :prefix-icon="null"
              :class="{ 'date-warning-danger': isETBDanger(row.etb) }"
              @change="handleFieldChange(row)"
              @blur="handleFieldBlur(row)"
              clearable
              :disabled="row.status === 'submitted' || row.status === 'confirmed'"
            />
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="etd" 
          label="ETD" 
          width="80"
          v-if="getColumnVisible('etd')"
        >
          <template #default="{ row }">
            <el-date-picker
              v-model="row.etd"
              type="datetime"
              size="small"
              placeholder="Date Time"
              format="MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 100%"
              :prefix-icon="null"
              @change="handleFieldChange(row)"
              @blur="handleFieldBlur(row)"
              clearable
              :disabled="row.status === 'submitted' || row.status === 'confirmed'"
            />
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="pWindow" 
          label="P Window" 
          width="80"
          v-if="getColumnVisible('pWindow')"
        >
          <template #default="{ row }">
            <el-date-picker
              v-model="row.pWindow"
              type="date"
              size="small"
              placeholder="Date"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 100%"
              :class="{ 'date-warning-danger': isPWindowDanger(row.pWindow) }"
              @change="handleFieldChange(row)"
              @blur="handleFieldBlur(row)"
              clearable
              :disabled="row.status === 'submitted' || row.status === 'confirmed'"
            />
          </template>
        </el-table-column>
        
        <!-- SPRO & Amt 合并列 -->
        <el-table-column 
          prop="spro" 
          label="SPRO&Amt" 
          width="110"
          v-if="getColumnVisible('spro')"
        >
          <template #default="{ row }">
            <div class="merged-cell">
              <el-select 
                v-model="row.spro" 
                size="small" 
                placeholder="Select"
                style="width: 50%;"
                @change="handleFieldChange(row)"
                @blur="handleFieldBlur(row)"
                :class="{
                  'status-n': row.spro === 'N',
                  'status-a': row.spro === 'A',
                  'status-p': row.spro === 'P'
                }"
                :disabled="row.status === 'submitted' || row.status === 'confirmed'"
              >
                <el-option label="N" value="N" />
                <el-option label="A" value="A" />
                <el-option label="P" value="P" />
              </el-select>
              <input 
                v-model="row.sproAmt" 
                type="number"
                class="cell-input merged-input"
                :disabled="row.spro === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
              />
            </div>
          </template>
        </el-table-column>
        
        <!-- Sludge & Amt & SQty 合并列 -->
        <el-table-column 
          prop="sludge" 
          label="Sludge&Amt&SQty" 
          width="150"
          v-if="getColumnVisible('sludge')"
        >
          <template #default="{ row }">
            <div class="merged-cell-three">
              <el-select 
                v-model="row.sludge" 
                size="small" 
                placeholder="Select"
                style="width: 33%;"
                @change="handleFieldChange(row)"
                @blur="handleFieldBlur(row)"
                :class="{
                  'status-n': row.sludge === 'N',
                  'status-a': row.sludge === 'A',
                  'status-p': row.sludge === 'P'
                }"
                :disabled="row.status === 'submitted' || row.status === 'confirmed'"
              >
                <el-option label="N" value="N" />
                <el-option label="A" value="A" />
                <el-option label="P" value="P" />
              </el-select>
              <input 
                v-model="row.sludgeAmt" 
                type="number"
                class="cell-input merged-input-three"
                :disabled="row.sludge === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
                placeholder="Amt"
              />
              <input 
                v-model="row.sQty" 
                type="number"
                class="cell-input merged-input-three"
                :disabled="row.sludge === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
                placeholder="SQty"
              />
            </div>
          </template>
        </el-table-column>
        
        <!-- Garbage & Amt & GQty 合并列 -->
        <el-table-column 
          prop="garbage" 
          label="Garbage&Amt&GQty" 
          width="150"
          v-if="getColumnVisible('garbage')"
        >
          <template #default="{ row }">
            <div class="merged-cell-three">
              <el-select 
                v-model="row.garbage" 
                size="small" 
                placeholder="Select"
                style="width: 33%;"
                @change="handleFieldChange(row)"
                @blur="handleFieldBlur(row)"
                :class="{
                  'status-n': row.garbage === 'N',
                  'status-a': row.garbage === 'A',
                  'status-p': row.garbage === 'P'
                }"
                :disabled="row.status === 'submitted' || row.status === 'confirmed'"
              >
                <el-option label="N" value="N" />
                <el-option label="A" value="A" />
                <el-option label="P" value="P" />
              </el-select>
              <input 
                v-model="row.garbageAmt" 
                type="number"
                class="cell-input merged-input-three"
                :disabled="row.garbage === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
                placeholder="Amt"
              />
              <input 
                v-model="row.gQty" 
                type="number"
                class="cell-input merged-input-three"
                :disabled="row.garbage === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
                placeholder="GQty"
              />
            </div>
          </template>
        </el-table-column>
        
        <!-- MC & Amt 合并列 -->
        <el-table-column 
          prop="mc" 
          label="MC&Amt" 
          width="110"
          v-if="getColumnVisible('mc')"
        >
          <template #default="{ row }">
            <div class="merged-cell">
              <el-select 
                v-model="row.mc" 
                size="small" 
                placeholder="Select"
                style="width: 50%;"
                @change="handleFieldChange(row)"
                @blur="handleFieldBlur(row)"
                :class="{
                  'status-n': row.mc === 'N',
                  'status-a': row.mc === 'A',
                  'status-p': row.mc === 'P'
                }"
                :disabled="row.status === 'submitted' || row.status === 'confirmed'"
              >
                <el-option label="N" value="N" />
                <el-option label="A" value="A" />
                <el-option label="P" value="P" />
              </el-select>
              <input 
                v-model="row.mcAmt" 
                type="number"
                class="cell-input merged-input"
                :disabled="row.mc === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
              />
            </div>
          </template>
        </el-table-column>
        
        <!-- Others & Amt 合并列 -->
        <el-table-column 
          prop="others" 
          label="Others&Amt" 
          width="110"
          v-if="getColumnVisible('others')"
        >
          <template #default="{ row }">
            <div class="merged-cell">
              <el-select 
                v-model="row.others" 
                size="small" 
                placeholder="Select"
                style="width: 50%;"
                @change="handleFieldChange(row)"
                @blur="handleFieldBlur(row)"
                :class="{
                  'status-n': row.others === 'N',
                  'status-a': row.others === 'A',
                  'status-p': row.others === 'P'
                }"
                :disabled="row.status === 'submitted' || row.status === 'confirmed'"
              >
                <el-option label="N" value="N" />
                <el-option label="A" value="A" />
                <el-option label="P" value="P" />
              </el-select>
              <input 
                v-model="row.othersAmt" 
                type="number"
                class="cell-input merged-input"
                :disabled="row.others === 'N' || row.status === 'submitted' || row.status === 'confirmed'"
                @input="handleAmountChange(row)"
                @blur="handleFieldBlur(row)"
                step="any"
              />
            </div>
          </template>
        </el-table-column>
        
        <el-table-column 
          prop="opsRemarks" 
          label="Ops Remarks" 
          width="130"
          v-if="getColumnVisible('opsRemarks')"
        >
          <template #default="{ row }">
            <el-input 
              v-model="row.opsRemarks" 
              type="textarea"
              size="small"
              placeholder="Remarks"
              :rows="2"
              @input="handleFieldChange(row)"
              @blur="handleFieldBlur(row)"
              class="small-font-input"
              style="width: 100%;"
              :disabled="row.status === 'submitted' || row.status === 'confirmed'"
            />
          </template>
        </el-table-column>
        
        <!-- INV Amt -->
        <el-table-column 
          prop="invAmount" 
          label="INV Amt" 
          width="65"
          v-if="getColumnVisible('invAmount')"
        >
          <template #default="{ row }">
            <el-input 
              :model-value="calculateInvAmount(row)"
              size="small"
              placeholder="Auto Calculate"
              style="width: 100%"
              class="small-font-input inv-amount"
              readonly
              disabled
            />
          </template>
        </el-table-column>
        
        <!-- Action 列 -->
        <el-table-column label="Action" width="80" align="center">
          <template #default="{ row }">
            <!-- 已提交财务状态 - 只显示标签 -->
            <template v-if="row.status === 'submitted'">
              <span class="status-badge status-submitted">Submitted</span>
            </template>
            <!-- 已确认状态 -->
            <template v-else-if="row.status === 'confirmed'">
              <span class="status-badge status-confirmed">Confirmed</span>
            </template>
            <!-- 待提交状态 -->
            <template v-else>
              <el-button 
                type="success" 
                size="small" 
                @click="handleSubmitToFinance(row)" 
                style="font-size: 10px; padding: 2px 6px; height: 20px;"
                :disabled="isStatusP(row)"
              >
                Submit
              </el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- New Order Dialog -->
    <el-dialog
      v-model="dialogVisible"
      title="Create New Order"
      width="1400px"
      :close-on-click-modal="false"
      destroy-on-close
      @close="resetNewOrderForm"
    >
      <div class="dialog-form-container">
        <div class="form-row">
          <!-- Order No -->
          <div class="form-group" style="width: 110px;">
            <label class="form-label required">Order No</label>
            <div class="order-no-wrapper">
              <span class="order-no-prefix">NSOR</span>
              <input 
                type="text" 
                v-model="newOrderForm.orderNo" 
                @input="formatOrderNo"
                @blur="padOrderNo"
                placeholder="Max 5"
                maxlength="5"
                class="form-input order-no-input"
                :class="{ 
                  'input-error': newOrderErrors.orderNo && newOrderErrors.orderNo !== '✓ Available' && !newOrderErrors.orderNo.includes('Checking'),
                  'input-success': newOrderErrors.orderNo === '✓ Available',
                  'input-checking': newOrderErrors.orderNo && newOrderErrors.orderNo.includes('Checking')
                }"
              />
            </div>
            <span v-if="newOrderErrors.orderNo" class="error-text" :class="{ 
              'success-text': newOrderErrors.orderNo === '✓ Available',
              'checking-text': newOrderErrors.orderNo && newOrderErrors.orderNo.includes('Checking')
            }">
              {{ newOrderErrors.orderNo }}
            </span>
          </div>

          <!-- Customer -->
          <div class="form-group" style="width: 120px;">
            <label class="form-label required">Customer</label>
            <input 
              type="text" 
              v-model="newOrderForm.customer" 
              @input="onCustomerInput"
              @blur="validateCustomer"
              placeholder="Select or input"
              class="form-input"
              :class="{ 'input-error': newOrderErrors.customer }"
              list="customer-list"
              autocomplete="off"
            />
            <datalist id="customer-list">
              <option v-for="cust in customerList" :key="cust" :value="cust" />
            </datalist>
            <span v-if="newOrderErrors.customer" class="error-text">{{ newOrderErrors.customer }}</span>
          </div>

          <!-- Vessel -->
          <div class="form-group" style="width: 160px;">
            <label class="form-label required">Vessel</label>
            <input 
              type="text" 
              v-model="newOrderForm.vessel" 
              @input="onVesselInput"
              @blur="validateVessel"
              placeholder="Vessel name"
              class="form-input"
              :class="{ 'input-error': newOrderErrors.vessel }"
            />
            <span v-if="newOrderErrors.vessel" class="error-text">{{ newOrderErrors.vessel }}</span>
          </div>

          <!-- IMO -->
          <div class="form-group" style="width: 100px;">
            <label class="form-label required">IMO</label>
            <input 
              type="text" 
              v-model="newOrderForm.imo" 
              @input="onIMOInput"
              @blur="validateIMO"
              placeholder="7 digits"
              maxlength="7"
              class="form-input"
              :class="{ 'input-error': newOrderErrors.imo }"
            />
            <span v-if="newOrderErrors.imo" class="error-text">{{ newOrderErrors.imo }}</span>
          </div>

          <!-- Type -->
          <div class="form-group" style="width: 180px;">
            <label class="form-label required">Type</label>
            <select v-model="newOrderForm.type" class="form-select" :class="{ 'input-error': newOrderErrors.type }">
              <option value="">Please Select</option>
              <option value="Vehicles Carrier">Vehicles Carrier</option>
              <option value="Ro-Ro Cargo Ship">Ro-Ro Cargo Ship</option>
              <option value="Passenger/Ro-Ro Ship (vehicles)">Passenger/Ro-Ro Ship (vehicles)</option>
              <option value="Container Ro-Ro Cargo Ship">Container Ro-Ro Cargo Ship</option>
              <option value="Bulk Carrier">Bulk Carrier</option>
              <option value="Crude Oil Tanker">Crude Oil Tanker</option>
              <option value="Chemical/Oil Products Tanker">Chemical/Oil Products Tanker</option>
              <option value="Container Ship">Container Ship</option>
              <option value="General Cargo Ship">General Cargo Ship</option>
              <option value="Refrigerated Cargo Ship">Refrigerated Cargo Ship</option>
            </select>
            <span v-if="newOrderErrors.type" class="error-text">{{ newOrderErrors.type }}</span>
          </div>

          <!-- Port -->
          <div class="form-group" style="width: 150px;">
            <label class="form-label required">Port</label>
            <input 
              type="text" 
              v-model="newOrderForm.port" 
              @input="onPortInput"
              @blur="validatePort"
              placeholder="Select or input"
              class="form-input"
              :class="{ 'input-error': newOrderErrors.port }"
              list="port-list"
              autocomplete="off"
            />
            <datalist id="port-list">
              <option v-for="port in portList" :key="port" :value="port" />
            </datalist>
            <span v-if="newOrderErrors.port" class="error-text">{{ newOrderErrors.port }}</span>
          </div>

          <!-- P Window -->
          <div class="form-group" style="width: 120px;">
            <label class="form-label">P Window</label>
            <input 
              type="date" 
              v-model="newOrderForm.pWindow" 
              class="form-input"
            />
          </div>

          <!-- R Level -->
          <div class="form-group" style="width: 140px;">
            <label class="form-label">R Level</label>
            <select v-model="newOrderForm.rLevel" class="form-select">
              <option value="">Please Select</option>
              <option value="H">H</option>
              <option value="S">S</option>
              <option value="L">L</option>
            </select>
          </div>

          <!-- Ops Remarks -->
          <div class="form-group" style="flex: 1; min-width: 140px;">
            <label class="form-label">Ops Remarks</label>
            <input 
              type="text" 
              v-model="newOrderForm.opsRemarks" 
              placeholder="Remarks"
              class="form-input"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">Cancel</el-button>
          <el-button 
            type="primary" 
            @click="submitNewOrder" 
            :loading="newOrderSubmitting"
            :disabled="!isNewOrderValid"
          >
            Submit
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Setting, Sort, Search, Plus } from '@element-plus/icons-vue'

// ============================================================
// 页面状态
// ============================================================
const loading = ref(false)
const orderList = ref([])
const sortedData = ref([])

// ============================================================
// Dialog 状态
// ============================================================
const dialogVisible = ref(false)
const newOrderSubmitting = ref(false)

// ============================================================
// 新建订单表单
// ============================================================
const newOrderForm = ref({
  orderNo: '',
  customer: '',
  vessel: '',
  imo: '',
  type: '',
  port: '',
  pWindow: '',
  rLevel: '',
  opsRemarks: ''
})

const newOrderErrors = ref({
  orderNo: '',
  customer: '',
  vessel: '',
  imo: '',
  type: '',
  port: ''
})

const orderExists = ref(false)
const customerList = ref([])
const portList = ref([])

// ============================================================
// 计算属性 - 表单验证
// ============================================================
const isNewOrderValid = computed(() => {
  const orderNoValid = newOrderForm.value.orderNo && 
                       newOrderForm.value.orderNo.length === 5 && 
                       !orderExists.value &&
                       newOrderErrors.value.orderNo === '✓ Available'
  
  const otherFieldsValid = !newOrderErrors.value.customer && 
                           !newOrderErrors.value.vessel && 
                           !newOrderErrors.value.imo && 
                           !newOrderErrors.value.type && 
                           !newOrderErrors.value.port &&
                           newOrderForm.value.customer &&
                           newOrderForm.value.vessel &&
                           newOrderForm.value.imo &&
                           newOrderForm.value.type &&
                           newOrderForm.value.port
  
  return orderNoValid && otherFieldsValid
})

// ============================================================
// 完整订单号
// ============================================================
const fullOrderNo = computed(() => {
  if (!newOrderForm.value.orderNo) return 'NSOR' + '0'.repeat(5)
  const num = parseInt(newOrderForm.value.orderNo) || 0
  return 'NSOR' + String(num).padStart(5, '0')
})

// ============================================================
// 搜索相关
// ============================================================
const searchKeyword = ref('')
const searchResults = ref([])
const isSearchActive = ref(false)

// ============================================================
// 排序相关
// ============================================================
const sortFields = ref([
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' },
  { field: '', order: 'ascending' }
])

const sortableColumns = [
  { prop: 'orderNo', label: 'Order No' },
  { prop: 'customer', label: 'Customer' },
  { prop: 'port', label: 'Port' },
  { prop: 'etb', label: 'ETB' },
  { prop: 'spro', label: 'SPRO' },
  { prop: 'sludge', label: 'Sludge' },
  { prop: 'garbage', label: 'Garbage' },
  { prop: 'mc', label: 'MC' },
  { prop: 'others', label: 'Others' }
]

// ============================================================
// 列显示控制
// ============================================================
const columns = ref([
  { prop: 'orderNo', label: 'Order No', visible: true },
  { prop: 'customer', label: 'Customer', visible: true },
  { prop: 'vessel', label: 'Vessel', visible: true },
  { prop: 'imo', label: 'IMO', visible: true },
  { prop: 'type', label: 'Type', visible: true },
  { prop: 'port', label: 'Port', visible: true },
  { prop: 'etb', label: 'ETB', visible: true },
  { prop: 'etd', label: 'ETD', visible: true },
  { prop: 'pWindow', label: 'P Window', visible: true },
  { prop: 'spro', label: 'SPRO&Amt', visible: true },
  { prop: 'sludge', label: 'Sludge&Amt&SQty', visible: true },
  { prop: 'garbage', label: 'Garbage&Amt&GQty', visible: true },
  { prop: 'mc', label: 'MC&Amt', visible: true },
  { prop: 'others', label: 'Others&Amt', visible: true },
  { prop: 'opsRemarks', label: 'Ops Remarks', visible: true },
  { prop: 'invAmount', label: 'INV Amt', visible: true }
])

const visibleColumns = computed(() => columns.value)

const getColumnVisible = (prop) => {
  const col = columns.value.find(c => c.prop === prop)
  return col ? col.visible : true
}

// ============================================================
// 显示数据 - 排序：pending(待提交) > submitted(已提交) > confirmed(已确认)
// ============================================================
const displayData = computed(() => {
  const data = sortedData.value
  const orderWeight = {
    'pending': 0,
    'submitted': 1,
    'confirmed': 2
  }
  return [...data].sort((a, b) => {
    const weightA = orderWeight[a.status || 'pending'] || 0
    const weightB = orderWeight[b.status || 'pending'] || 0
    return weightA - weightB
  })
})

// ============================================================
// ========== New Order 表单方法 ==========
// ============================================================

// -------- Order No 处理 --------
const formatOrderNo = () => {
  newOrderForm.value.orderNo = newOrderForm.value.orderNo.replace(/\D/g, '')
  if (newOrderForm.value.orderNo.length > 5) {
    newOrderForm.value.orderNo = newOrderForm.value.orderNo.slice(0, 5)
  }
  
  if (newOrderForm.value.orderNo && newOrderForm.value.orderNo.length > 0) {
    const num = parseInt(newOrderForm.value.orderNo)
    if (num >= 1 && num <= 99999) {
      newOrderErrors.value.orderNo = ''
    } else {
      newOrderErrors.value.orderNo = 'Please enter 1-99999'
      orderExists.value = false
    }
  } else {
    newOrderErrors.value.orderNo = ''
    orderExists.value = false
  }
}

const padOrderNo = () => {
  if (newOrderForm.value.orderNo && newOrderForm.value.orderNo.length > 0) {
    const num = parseInt(newOrderForm.value.orderNo)
    if (num >= 1 && num <= 99999) {
      newOrderForm.value.orderNo = String(num).padStart(5, '0')
      newOrderErrors.value.orderNo = ''
      checkOrderExists(fullOrderNo.value)
    } else {
      newOrderErrors.value.orderNo = 'Please enter 1-99999'
      orderExists.value = false
    }
  } else {
    newOrderErrors.value.orderNo = ''
    orderExists.value = false
  }
}

// -------- 检查订单号是否存在（查询 new_orders 表） --------
const checkOrderExists = async (orderNo) => {
  try {
    newOrderErrors.value.orderNo = '⏳ Checking...'
    
    const response = await fetch(`${getApiBase()}/api/auth/check-order-new/${orderNo}`, {
      headers: getHeaders()
    })
    const result = await response.json()
    
    if (result.exists) {
      newOrderErrors.value.orderNo = '❌ Order number already exists!'
      orderExists.value = true
    } else {
      newOrderErrors.value.orderNo = '✓ Available'
      orderExists.value = false
    }
  } catch (error) {
    console.error('检查订单号失败:', error)
    newOrderErrors.value.orderNo = '⚠️ Check failed'
    orderExists.value = false
  }
}

// -------- Customer 处理 --------
const onCustomerInput = () => {
  fetchCustomers(newOrderForm.value.customer)
}

const validateCustomer = () => {
  const regex = /^[A-Za-z\s]*$/
  if (newOrderForm.value.customer && !regex.test(newOrderForm.value.customer)) {
    newOrderErrors.value.customer = 'Only letters and spaces'
  } else if (newOrderForm.value.customer && newOrderForm.value.customer.length < 2) {
    newOrderErrors.value.customer = 'At least 2 characters'
  } else {
    newOrderErrors.value.customer = ''
  }
}

// -------- Vessel 处理 --------
const onVesselInput = () => {
  if (!newOrderForm.value.vessel || newOrderForm.value.vessel.trim() === '') return
  
  if (vesselTimer) clearTimeout(vesselTimer)
  vesselTimer = setTimeout(() => {
    fetchVesselDetails({ vessel_name: newOrderForm.value.vessel.trim() })
  }, 500)
}

const validateVessel = () => {
  const regex = /^[A-Za-z0-9\s]*$/
  if (newOrderForm.value.vessel && !regex.test(newOrderForm.value.vessel)) {
    newOrderErrors.value.vessel = 'Only letters, numbers and spaces'
  } else if (newOrderForm.value.vessel && newOrderForm.value.vessel.length < 2) {
    newOrderErrors.value.vessel = 'At least 2 characters'
  } else {
    newOrderErrors.value.vessel = ''
  }
}

// -------- IMO 处理 --------
const onIMOInput = () => {
  if (!newOrderForm.value.imo || newOrderForm.value.imo.trim() === '') return
  
  if (newOrderForm.value.imo.length === 7) {
    if (imoTimer) clearTimeout(imoTimer)
    imoTimer = setTimeout(() => {
      fetchVesselDetails({ imo: newOrderForm.value.imo.trim() })
    }, 500)
  }
}

const validateIMO = () => {
  newOrderForm.value.imo = newOrderForm.value.imo.replace(/\D/g, '')
  if (newOrderForm.value.imo.length > 7) {
    newOrderForm.value.imo = newOrderForm.value.imo.slice(0, 7)
  }
  if (newOrderForm.value.imo && newOrderForm.value.imo.length === 7) {
    newOrderErrors.value.imo = ''
  } else if (newOrderForm.value.imo && newOrderForm.value.imo.length > 0) {
    newOrderErrors.value.imo = 'IMO must be 7 digits'
  } else {
    newOrderErrors.value.imo = ''
  }
}

// -------- Port 处理 --------
const onPortInput = () => {
  fetchPorts(newOrderForm.value.port)
}

const validatePort = () => {
  if (newOrderForm.value.port && newOrderForm.value.port.length < 2) {
    newOrderErrors.value.port = 'At least 2 characters'
  } else {
    newOrderErrors.value.port = ''
  }
}

// -------- 获取客户列表 --------
const fetchCustomers = async (keyword = '') => {
  try {
    const url = keyword 
      ? `${getApiBase()}/api/auth/customers?keyword=${encodeURIComponent(keyword)}`
      : `${getApiBase()}/api/auth/customers`
    
    const response = await fetch(url, { headers: getHeaders() })
    const result = await response.json()
    if (result.success) {
      customerList.value = result.data.map(item => item.name)
    }
  } catch (error) {
    console.error('获取客户列表失败:', error)
  }
}

// -------- 获取港口列表 --------
const fetchPorts = async (keyword = '') => {
  try {
    const url = keyword 
      ? `${getApiBase()}/api/auth/ports?keyword=${encodeURIComponent(keyword)}`
      : `${getApiBase()}/api/auth/ports`
    
    const response = await fetch(url, { headers: getHeaders() })
    const result = await response.json()
    if (result.success) {
      portList.value = result.data.map(item => item.name)
    }
  } catch (error) {
    console.error('获取港口列表失败:', error)
  }
}

// -------- 获取船只详情 --------
let vesselTimer = null
let imoTimer = null

const fetchVesselDetails = async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await fetch(`${getApiBase()}/api/auth/vessel-details?${queryString}`, {
      headers: getHeaders()
    })
    const result = await response.json()
    
    if (result.success && result.data) {
      if (result.data.customer) {
        newOrderForm.value.customer = result.data.customer
        newOrderErrors.value.customer = ''
      }
      if (result.data.vessel_name && !newOrderForm.value.vessel) {
        newOrderForm.value.vessel = result.data.vessel_name
        newOrderErrors.value.vessel = ''
      }
      if (result.data.imo && !newOrderForm.value.imo) {
        newOrderForm.value.imo = result.data.imo
        newOrderErrors.value.imo = ''
      }
      if (result.data.type) {
        newOrderForm.value.type = result.data.type
        newOrderErrors.value.type = ''
      }
    }
  } catch (error) {
    console.error('获取船只详情失败:', error)
  }
}

// -------- 提交新订单 (同时写入 new_orders 和 order_monitoring) --------
const submitNewOrder = async () => {
  if (!isNewOrderValid.value) {
    ElMessage.warning('请正确填写所有必填字段')
    return
  }

  newOrderSubmitting.value = true

  try {
    const operator = localStorage.getItem('username') || 'system'
    const orderNo = fullOrderNo.value
    const invNo = 'NSIV' + orderNo.slice(-5)

    const rLevelMap = { 'H': 'High', 'S': 'Standard', 'L': 'Low' }
    const rLevelDb = rLevelMap[newOrderForm.value.rLevel] || null

    // ✅ 1. 先检查 new_orders 表是否存在该订单号
    const checkResponse = await fetch(`${getApiBase()}/api/auth/check-order-new/${orderNo}`, {
      headers: getHeaders()
    })
    const checkResult = await checkResponse.json()
    
    if (checkResult.exists) {
      ElMessage.error(`❌ 订单号 ${orderNo} 已存在，请使用其他号码`)
      newOrderSubmitting.value = false
      return
    }

    // ✅ 2. 准备订单数据 - 字段与后端完全匹配
    const orderData = {
      operator: operator,
      order_no: orderNo,
      customer: newOrderForm.value.customer.trim(),
      vessel: newOrderForm.value.vessel.trim(),
      imo: newOrderForm.value.imo,
      type: newOrderForm.value.type,
      port: newOrderForm.value.port.trim(),
      p_window: newOrderForm.value.pWindow || null,
      r_level: rLevelDb,
      ops_remarks: newOrderForm.value.opsRemarks || null,
      status: 'pending',
      inv_no: invNo,
      inv_amount: 0,
      s_qty: 0,
      g_qty: 0
    }

    console.log('📦 提交订单数据:', JSON.stringify(orderData, null, 2))

    // ✅ 3. 调用后端接口同时写入两个表
    const response = await fetch(`${getApiBase()}/api/auth/orders-with-monitoring`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(orderData)
    })

    const result = await response.json()

    if (result.success) {
      ElMessage.success(`✅ 订单创建成功！订单号: ${orderNo}`)
      dialogVisible.value = false
      resetNewOrderForm()
      await loadOrderList()
    } else {
      ElMessage.error(`❌ 创建失败: ${result.message || '未知错误'}`)
    }
  } catch (error) {
    console.error('提交订单失败:', error)
    ElMessage.error('❌ 创建失败，请重试')
  } finally {
    newOrderSubmitting.value = false
  }
}

// -------- 重置表单 --------
const resetNewOrderForm = () => {
  newOrderForm.value = {
    orderNo: '',
    customer: '',
    vessel: '',
    imo: '',
    type: '',
    port: '',
    pWindow: '',
    rLevel: '',
    opsRemarks: ''
  }
  newOrderErrors.value = {
    orderNo: '',
    customer: '',
    vessel: '',
    imo: '',
    type: '',
    port: ''
  }
  orderExists.value = false
  if (vesselTimer) clearTimeout(vesselTimer)
  if (imoTimer) clearTimeout(imoTimer)
}

// ============================================================
// ========== 搜索逻辑 ==========
// ============================================================
const applySearch = () => {
  const keyword = searchKeyword.value.trim().toLowerCase()
  if (!keyword) {
    isSearchActive.value = false
    searchResults.value = []
    applyFilters()
    ElMessage.info('请输入搜索关键词')
    return
  }
  
  const results = orderList.value.filter(row => {
    const searchFields = [
      row.orderNo || '',
      row.customer || '',
      row.vessel || '',
      row.port || ''
    ]
    return searchFields.some(field => 
      field.toLowerCase().includes(keyword)
    )
  })
  
  if (results.length === 0) {
    ElMessage.info('未找到匹配的订单')
  } else {
    ElMessage.success(`找到 ${results.length} 个匹配订单`)
  }
  
  isSearchActive.value = true
  searchResults.value = results
  applyFilters()
}

const clearSearch = () => {
  searchKeyword.value = ''
  isSearchActive.value = false
  searchResults.value = []
  applyFilters()
  ElMessage.info('已清空搜索条件')
}

// ============================================================
// ========== 排序逻辑 ==========
// ============================================================
const isFieldUsed = (currentField, currentIndex) => {
  return sortFields.value.some((item, index) => 
    index !== currentIndex && item.field === currentField && currentField !== ''
  )
}

const applySort = () => {
  const activeSorts = sortFields.value.filter(item => item.field !== '')
  if (activeSorts.length === 0) {
    applyFilters()
    ElMessage.info('没有选择排序字段，显示原始数据')
    return
  }
  
  const sourceData = isSearchActive.value ? searchResults.value : orderList.value
  const sorted = [...sourceData]
  sorted.sort((a, b) => {
    for (const sortItem of activeSorts) {
      const field = sortItem.field
      const order = sortItem.order
      
      let valA = a[field]
      let valB = b[field]
      
      if (valA === null || valA === undefined || valA === '') valA = ''
      if (valB === null || valB === undefined || valB === '') valB = ''
      
      if (typeof valA === 'number' && typeof valB === 'number') {
        if (valA !== valB) {
          return order === 'ascending' ? valA - valB : valB - valA
        }
        continue
      }
      
      valA = String(valA).toLowerCase()
      valB = String(valB).toLowerCase()
      
      if (valA !== valB) {
        if (order === 'ascending') {
          return valA.localeCompare(valB)
        } else {
          return valB.localeCompare(valA)
        }
      }
    }
    return 0
  })
  sortedData.value = sorted
  ElMessage.success(`已按 ${activeSorts.length} 个字段排序`)
}

const clearSort = () => {
  sortFields.value = [
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' },
    { field: '', order: 'ascending' }
  ]
  applyFilters()
  ElMessage.info('已清空排序条件')
}

// ============================================================
// ========== 综合过滤 ==========
// ============================================================
const applyFilters = () => {
  const sourceData = isSearchActive.value ? searchResults.value : orderList.value
  const activeSorts = sortFields.value.filter(item => item.field !== '')
  
  if (activeSorts.length === 0) {
    sortedData.value = sourceData
    return
  }
  
  const sorted = [...sourceData]
  sorted.sort((a, b) => {
    for (const sortItem of activeSorts) {
      const field = sortItem.field
      const order = sortItem.order
      
      let valA = a[field]
      let valB = b[field]
      
      if (valA === null || valA === undefined || valA === '') valA = ''
      if (valB === null || valB === undefined || valB === '') valB = ''
      
      if (typeof valA === 'number' && typeof valB === 'number') {
        if (valA !== valB) {
          return order === 'ascending' ? valA - valB : valB - valA
        }
        continue
      }
      
      valA = String(valA).toLowerCase()
      valB = String(valB).toLowerCase()
      
      if (valA !== valB) {
        if (order === 'ascending') {
          return valA.localeCompare(valB)
        } else {
          return valB.localeCompare(valA)
        }
      }
    }
    return 0
  })
  sortedData.value = sorted
}

// ============================================================
// ========== 表格内方法 ==========
// ============================================================

// -------- 判断是否有状态为 P --------
const isStatusP = (row) => {
  return row.spro === 'P' || 
         row.sludge === 'P' || 
         row.garbage === 'P' || 
         row.mc === 'P' || 
         row.others === 'P'
}

// -------- 计算 INV Amount --------
const calculateInvAmount = (row) => {
  const parseNum = (val) => {
    if (val === '' || val === null || val === undefined) return 0
    const num = parseFloat(val)
    return isNaN(num) ? 0 : num
  }
  
  const total = parseNum(row.sproAmt) + 
                parseNum(row.sludgeAmt) + 
                parseNum(row.garbageAmt) + 
                parseNum(row.mcAmt) + 
                parseNum(row.othersAmt)
  
  return total
}

// -------- ETB 危险判断 --------
const isETBDanger = (dateStr) => {
  if (!dateStr) return false
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return false
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const targetDate = new Date(date)
  targetDate.setHours(0, 0, 0, 0)
  
  const diffDays = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24))
  
  return diffDays <= 4
}

// -------- P Window 危险判断 --------
const isPWindowDanger = (dateStr) => {
  if (!dateStr) return false
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return false
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const targetDate = new Date(date)
  targetDate.setHours(0, 0, 0, 0)
  
  const diffDays = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24))
  
  return diffDays <= 10
}

// ============================================================
// ========== 提交到财务 ==========
// ============================================================
const handleSubmitToFinance = async (row) => {
  // 验证订单号格式
  if (!/^NSOR\d{5}$/.test(row.orderNo)) {
    ElMessage.warning('订单号格式不正确')
    return
  }
  
  // 确认对话框
  ElMessageBox.confirm(
    `确定要将订单 ${row.orderNo} 提交到财务吗？\n提交后订单将不可编辑。`,
    '提交财务确认',
    {
      confirmButtonText: '确定提交',
      cancelButtonText: '取消',
      type: 'info',
    }
  ).then(async () => {
    loading.value = true
    try {
      // ✅ 先保存当前所有修改到数据库（确保数据最新）
      await saveRowToDatabase(row)
      
      // ✅ 然后调用提交接口
      const dbData = mapFrontendToDb(row)
      
      const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}/submit`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(dbData)
      })
      
      if (!response.ok) {
        throw new Error('提交失败')
      }
      
      const result = await response.json()
      
      if (result.success) {
        ElMessage.success(`✅ 订单 ${row.orderNo} 已提交到财务`)
        // ✅ 重新加载列表，获取最新状态
        await loadOrderList()
      } else {
        ElMessage.error(result.message || '提交失败')
      }
    } catch (error) {
      console.error('提交到财务失败:', error)
      ElMessage.error(`提交失败: ${error.message}`)
    } finally {
      loading.value = false
    }
  }).catch(() => {
    // 用户取消
    ElMessage.info('已取消提交')
  })
}

// -------- 财务确认 --------
const handleFinanceConfirm = async (row) => {
  ElMessageBox.confirm(
    `确认财务已收到订单 ${row.orderNo} 的款项吗？\n确认后订单将从列表中移除。`,
    '财务确认',
    {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'success',
    }
  ).then(async () => {
    loading.value = true
    try {
      row.status = 'confirmed'
      const dbData = mapFrontendToDb(row)
      
      const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}/confirm`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(dbData)
      })
      
      if (!response.ok) {
        throw new Error('确认失败')
      }
      
      const result = await response.json()
      
      if (result.success) {
        ElMessage.success(`订单 ${row.orderNo} 已确认完成`)
        const index = orderList.value.findIndex(item => item.id === row.id)
        if (index > -1) {
          orderList.value.splice(index, 1)
          if (isSearchActive.value) {
            const searchIndex = searchResults.value.findIndex(item => item.id === row.id)
            if (searchIndex > -1) {
              searchResults.value.splice(searchIndex, 1)
            }
          }
          applyFilters()
        }
      } else {
        ElMessage.error(result.message || '确认失败')
      }
    } catch (error) {
      ElMessage.error(`确认失败: ${error.message}`)
    } finally {
      loading.value = false
    }
  }).catch(() => {})
}

// -------- 财务拒绝 --------
const handleFinanceReject = async (row) => {
  ElMessageBox.confirm(
    `拒绝订单 ${row.orderNo} 的财务确认吗？\n拒绝后订单将恢复可编辑状态。`,
    '财务拒绝',
    {
      confirmButtonText: '确定拒绝',
      cancelButtonText: '取消',
      type: 'warning',
    }
  ).then(async () => {
    loading.value = true
    try {
      row.status = 'pending'
      const dbData = mapFrontendToDb(row)
      
      const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}/reject`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(dbData)
      })
      
      if (!response.ok) {
        throw new Error('拒绝失败')
      }
      
      const result = await response.json()
      
      if (result.success) {
        ElMessage.success(`订单 ${row.orderNo} 已拒绝，可重新编辑`)
        await loadOrderList()
      } else {
        ElMessage.error(result.message || '拒绝失败')
      }
    } catch (error) {
      ElMessage.error(`拒绝失败: ${error.message}`)
    } finally {
      loading.value = false
    }
  }).catch(() => {})
}

// ============================================================
// ========== API 配置 ==========
// ============================================================
const getApiBase = () => {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'
}

const getToken = () => {
  return localStorage.getItem('token')
}

const getHeaders = () => {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ============================================================
// ========== 数据映射 ==========
// ============================================================
const mapDbToFrontend = (dbData) => {
  return {
    id: dbData.id,
    no: dbData.no,
    orderNo: dbData.order_no || '',
    customer: dbData.customer || '',
    vessel: dbData.vessel || '',
    imo: dbData.imo || '',
    type: dbData.type || '',
    port: dbData.port || '',
    etb: dbData.etb || '',
    etd: dbData.etd || '',
    pWindow: dbData.p_window || '',
    spro: dbData.spro || 'N',
    sproAmt: dbData.spro_amt !== null && dbData.spro_amt !== undefined ? dbData.spro_amt : '',
    sludge: dbData.sludge || 'N',
    sludgeAmt: dbData.sludge_amt !== null && dbData.sludge_amt !== undefined ? dbData.sludge_amt : '',
    garbage: dbData.garbage || 'N',
    garbageAmt: dbData.garbage_amt !== null && dbData.garbage_amt !== undefined ? dbData.garbage_amt : '',
    mc: dbData.mc || 'N',
    mcAmt: dbData.mc_amt !== null && dbData.mc_amt !== undefined ? dbData.mc_amt : '',
    others: dbData.others || 'N',
    othersAmt: dbData.others_amt !== null && dbData.others_amt !== undefined ? dbData.others_amt : '',
    opsRemarks: dbData.ops_remarks || '',
    invAmount: dbData.inv_amount !== null && dbData.inv_amount !== undefined ? dbData.inv_amount : '',
    status: dbData.status || 'pending',
    sQty: dbData.s_qty !== null && dbData.s_qty !== undefined ? dbData.s_qty : '',
    gQty: dbData.g_qty !== null && dbData.g_qty !== undefined ? dbData.g_qty : ''
  }
}

const mapFrontendToDb = (frontData) => {
  const parseAmount = (value) => {
    if (value === '' || value === null || value === undefined) {
      return 0;
    }
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };
  
  const totalInvAmount = parseAmount(frontData.sproAmt) + 
                         parseAmount(frontData.sludgeAmt) + 
                         parseAmount(frontData.garbageAmt) + 
                         parseAmount(frontData.mcAmt) + 
                         parseAmount(frontData.othersAmt);
  
  return {
    order_no: frontData.orderNo,
    customer: frontData.customer || null,
    vessel: frontData.vessel || null,
    imo: frontData.imo || null,
    type: frontData.type || null,
    port: frontData.port || null,
    operator: frontData.operator || null,
    ops_remarks: frontData.opsRemarks || null,
    status: frontData.status || 'pending',
    p_window: frontData.pWindow || null,
    r_level: frontData.rLevel || null,
    etb: frontData.etb || null,
    etd: frontData.etd || null,
    no: frontData.no || null,
    spro: frontData.spro || 'N',
    spro_amt: parseAmount(frontData.sproAmt),
    sludge: frontData.sludge || 'N',
    sludge_amt: parseAmount(frontData.sludgeAmt),
    garbage: frontData.garbage || 'N',
    garbage_amt: parseAmount(frontData.garbageAmt),
    mc: frontData.mc || 'N',
    mc_amt: parseAmount(frontData.mcAmt),
    mc_remarks: frontData.mc_remarks || null,
    others: frontData.others || 'N',
    others_amt: parseAmount(frontData.othersAmt),
    inv_no: frontData.inv_no || null,
    inv_amount: totalInvAmount,
    s_qty: parseAmount(frontData.sQty),
    g_qty: parseAmount(frontData.gQty),
    updated_by: localStorage.getItem('username') || 'system'
  }
}

// ============================================================
// ========== 自动保存 (实时更新到 order_monitoring 表) ==========
// ============================================================
let updateTimer = null

const handleFieldChange = (row) => {
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
  updateTimer = setTimeout(async () => {
    await saveRowToDatabase(row)
    updateTimer = null
  }, 500)
}

const handleFieldBlur = (row) => {
  if (updateTimer) {
    clearTimeout(updateTimer)
    updateTimer = null
  }
  saveRowToDatabase(row)
}

const handleAmountChange = (row) => {
  handleFieldChange(row)
}

const saveRowToDatabase = async (row) => {
  try {
    const dbData = mapFrontendToDb(row)
    
    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(dbData)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('HTTP错误:', response.status, errorText)
      throw new Error(`HTTP ${response.status}: ${errorText}`)
    }
    
    const result = await response.json()
    
    if (result.success) {
      // 静默保存成功
    } else {
      ElMessage.error(`保存失败: ${result.message || '未知错误'}`)
    }
  } catch (error) {
    console.error('自动保存失败:', error)
    ElMessage.error(`订单 ${row.orderNo} 保存失败: ${error.message}`)
  }
}

// ============================================================
// ========== 加载订单列表 (从 order_monitoring 表) ==========
// ============================================================
const fetchOrders = async () => {
  try {
    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring`, {
      headers: getHeaders()
    })
    if (!response.ok) {
      if (response.status === 401) {
        ElMessage.error('认证已过期，请重新登录')
        return []
      }
      throw new Error('获取订单列表失败')
    }
    return await response.json()
  } catch (error) {
    console.error('获取订单列表错误:', error)
    throw error
  }
}

const loadOrderList = async () => {
  loading.value = true
  try {
    const data = await fetchOrders()
    if (data && Array.isArray(data)) {
      orderList.value = data.map(item => mapDbToFrontend(item))
      sortedData.value = orderList.value
    } else {
      orderList.value = []
      sortedData.value = []
    }
  } catch (error) {
    ElMessage.error('加载订单列表失败')
    orderList.value = []
    sortedData.value = []
  } finally {
    loading.value = false
  }
}

// ============================================================
// ========== 页面初始化 ==========
// ============================================================
onMounted(() => {
  loadOrderList()
})
</script>

<style>
.order-proceeding-page {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.table-container {
  flex: 1;
  overflow: auto;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: 2px 3px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  padding-bottom: 2px;
  border-bottom: 2px solid #ebeef5;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* ============================================================
   New Order 按钮 - 绿色（默认 success 类型就是绿色）
   ============================================================ */
.header-actions .el-button--success {
  background-color: #67c23a !important;
  border-color: #67c23a !important;
  color: #ffffff !important;
}

.header-actions .el-button--success:hover {
  background-color: #85ce61 !important;
  border-color: #85ce61 !important;
  color: #ffffff !important;
}

/* ============================================================
   Row Setting 按钮 - 绿色
   ============================================================ */
.header-actions .el-button--success.is-plain {
  color: #ffffff !important;
  background-color: #67c23a !important;
  border-color: #67c23a !important;
}

.header-actions .el-button--success.is-plain:hover {
  background-color: #85ce61 !important;
  border-color: #85ce61 !important;
  color: #ffffff !important;
}

.header-actions .el-button--success.is-plain.el-button--small {
  color: #ffffff !important;
  background-color: #67c23a !important;
  border-color: #67c23a !important;
}

.header-actions .el-button--success.is-plain.el-button--small:hover {
  background-color: #85ce61 !important;
  border-color: #85ce61 !important;
  color: #ffffff !important;
}

/* ============================================================
   Start 按钮字体白色
   ============================================================ */
.search-actions-inline .el-button--primary.is-plain,
.sorting-actions-inline .el-button--primary.is-plain {
  color: #ffffff !important;
  background-color: #409eff !important;
  border-color: #409eff !important;
}

.search-actions-inline .el-button--primary.is-plain:hover,
.sorting-actions-inline .el-button--primary.is-plain:hover {
  background-color: #66b1ff !important;
  border-color: #66b1ff !important;
  color: #ffffff !important;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f0f7ff;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #d9ecff;
}

.search-label {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #409eff;
  white-space: nowrap;
}

.search-container .el-input {
  width: 220px !important;
}

.search-container .el-input .el-input__wrapper {
  padding: 0 6px !important;
  height: 26px !important;
  min-height: 26px !important;
}

.search-container .el-input .el-input__inner {
  font-size: 11px !important;
  height: 24px !important;
  line-height: 24px !important;
}

.search-actions-inline {
  display: flex;
  align-items: center;
  gap: 2px;
}

.search-actions-inline .el-button {
  height: 22px;
  line-height: 22px;
}

.sorting-container {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
}

.sorting-label {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #e6a23c;
  white-space: nowrap;
}

.sorting-fields {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.sorting-field-item {
  display: flex;
  align-items: center;
  gap: 2px;
  background: white;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid #e8ecf1;
}

.sort-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #409eff;
  color: white;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 600;
  flex-shrink: 0;
}

.sorting-actions-inline {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 2px;
}

.sorting-actions-inline .el-button {
  height: 22px;
  line-height: 22px;
}

.column-setting-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding: 12px 16px;
  max-width: 600px;
  min-width: 300px;
}

.column-setting-item-horizontal {
  flex: 0 0 auto;
  white-space: nowrap;
}

.cell-text {
  display: block;
  padding: 0px 2px;
  color: #303133;
  font-weight: 400;
  font-size: 10px;
  text-align: center;
  width: 100%;
  min-height: 18px;
  line-height: 18px;
}

.cell-left {
  text-align: left !important;
}

.merged-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 0px 6px;
}

.merged-cell .el-select {
  flex: 0 0 48%;
}

.merged-cell .el-select .el-input__wrapper {
  padding: 0 4px !important;
  height: 28px !important;
  min-height: 28px !important;
}

.merged-cell .el-select .el-input__inner {
  font-size: 10px !important;
  font-weight: 400 !important;
  padding: 0 4px !important;
  height: 28px !important;
  line-height: 28px !important;
}

.merged-input {
  flex: 0 0 48%;
  width: 48% !important;
  height: 28px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 400;
  text-align: center;
  color: #303133;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.merged-input:focus {
  border-color: #409eff;
}

.merged-input:disabled {
  background-color: #f5f7fa;
  color: #999;
  cursor: not-allowed;
}

.merged-input::-webkit-inner-spin-button,
.merged-input::-webkit-outer-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}

.merged-input[type="number"] {
  -moz-appearance: textfield !important;
}

.merged-cell-three {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 0px 2px;
}

.merged-cell-three .el-select {
  flex: 0 0 33%;
}

.merged-cell-three .el-select .el-input__wrapper {
  padding: 0 4px !important;
  height: 28px !important;
  min-height: 28px !important;
}

.merged-cell-three .el-select .el-input__inner {
  font-size: 10px !important;
  font-weight: 400 !important;
  padding: 0 4px !important;
  height: 28px !important;
  line-height: 28px !important;
}

.merged-input-three {
  flex: 0 0 33%;
  width: 33% !important;
  height: 28px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0 2px;
  font-size: 9px;
  font-weight: 400;
  text-align: center;
  color: #303133;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.merged-input-three:focus {
  border-color: #409eff;
}

.merged-input-three:disabled {
  background-color: #f5f7fa;
  color: #999;
  cursor: not-allowed;
}

.merged-input-three::-webkit-inner-spin-button,
.merged-input-three::-webkit-outer-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}

.merged-input-three[type="number"] {
  -moz-appearance: textfield !important;
}

.merged-input-three::placeholder {
  color: #bbb;
  font-size: 8px;
}

.cell-input {
  width: 100%;
  height: 28px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 400;
  text-align: center;
  color: #303133;
  background: #fff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.cell-input:focus {
  border-color: #409eff;
}

.cell-input:disabled {
  background-color: #f5f7fa;
  color: #999;
  cursor: not-allowed;
}

.cell-input::-webkit-inner-spin-button,
.cell-input::-webkit-outer-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}

.cell-input[type="number"] {
  -moz-appearance: textfield !important;
}

.el-table td .date-warning-danger .el-input__wrapper {
  border-color: #dc143c !important;
  background-color: #ffebee !important;
  box-shadow: none !important;
}

.el-table td .date-warning-danger .el-input__inner {
  color: #dc143c !important;
  font-weight: 400 !important;
}

.el-table td .date-warning-danger .el-input__wrapper:hover {
  border-color: #dc143c !important;
  box-shadow: none !important;
}

.el-table td .date-warning-danger.is-focus .el-input__wrapper {
  border-color: #dc143c !important;
  box-shadow: none !important;
}

/* ============================================================
   ✅ 状态样式 - N（灰色）, A（绿色）, P（红色）
   ============================================================ */

/* N 状态 - 灰色背景，灰色字体 */
.el-table .status-n .el-input__wrapper {
  background-color: #f5f5f5 !important;
  border-color: #d9d9d9 !important;
}
.el-table .status-n .el-select__wrapper {
  background-color: #f5f5f5 !important;
  border-color: #d9d9d9 !important;
}
.el-table .status-n .el-select__selection {
  color: #999999 !important;
  font-weight: 400 !important;
  font-size: 10px !important;
}
.el-table .status-n .el-input .el-input__inner,
.el-table .status-n .el-input__wrapper .el-input__inner,
.el-table .status-n .el-select .el-input__inner {
  color: #999999 !important;
  font-weight: 400 !important;
  -webkit-text-fill-color: #999999 !important;
}

/* A 状态 - 绿色背景，白色字体 */
.el-table .status-a .el-input__wrapper {
  background-color: #07c160 !important;
  border-color: #07c160 !important;
}
.el-table .status-a .el-select__wrapper {
  background-color: #07c160 !important;
  border-color: #07c160 !important;
}
.el-table .status-a .el-select__selection {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 10px !important;
}
.el-table .status-a .el-input .el-input__inner,
.el-table .status-a .el-input__wrapper .el-input__inner,
.el-table .status-a .el-select .el-input__inner {
  color: #ffffff !important;
  font-weight: 700 !important;
  -webkit-text-fill-color: #ffffff !important;
}

/* P 状态 - 红色背景，白色字体 */
.el-table .status-p .el-input__wrapper {
  background-color: #de2910 !important;
  border-color: #de2910 !important;
  box-shadow: none !important;
}
.el-table .status-p .el-select__wrapper {
  background-color: #de2910 !important;
  border-color: #de2910 !important;
  box-shadow: none !important;
}
.el-table .status-p .el-select__selection {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 10px !important;
}
.el-table .status-p .el-input .el-input__inner,
.el-table .status-p .el-input__wrapper .el-input__inner,
.el-table .status-p .el-select .el-input__inner {
  color: #ffffff !important;
  font-weight: 700 !important;
  -webkit-text-fill-color: #ffffff !important;
}

/* 确保 select 下拉框中的选项也显示白色 */
.el-table .status-a .el-select .el-select__wrapper .el-select__selection,
.el-table .status-p .el-select .el-select__wrapper .el-select__selection {
  color: #ffffff !important;
}

.inv-amount .el-input__wrapper {
  background-color: #f0f9eb !important;
  border-color: #07c160 !important;
}

.inv-amount .el-input__inner {
  color: #07c160 !important;
  font-weight: 700 !important;
  font-size: 11px !important;
}

.status-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 2px;
}

.status-submitted {
  background: #fef6e0;
  color: #e6a23c;
}

.status-confirmed {
  background: #f0f9eb;
  color: #67c23a;
}

.el-table {
  font-size: 12px;
}

.el-table .cell {
  padding: 0px 2px !important;
  line-height: 16px !important;
  min-height: 24px !important;
}

.el-table td.el-table__cell {
  padding: 1px 0px !important;
}

.el-table th.el-table__cell {
  background-color: #3498db !important;
  color: #ffffff !important;
  padding: 0px 0px !important;
}

.el-table th.el-table__cell .cell {
  line-height: 26px !important;
  font-size: 10px !important;
  color: #ffffff !important;
}

.el-table__row {
  height: auto !important;
}

.el-table__row td {
  padding: 0 !important;
}

.el-table__body-wrapper .el-table__row td .cell {
  padding: 0px 2px !important;
  line-height: 16px !important;
  min-height: auto !important;
}

.el-table__body-wrapper {
  overflow-y: auto;
}

.el-date-editor {
  width: 100% !important;
  height: 24px !important;
}

.el-date-editor .el-input__prefix {
  display: none !important;
}

.el-date-editor .el-input__suffix {
  display: none !important;
}

.el-table__header-wrapper {
  background: #f5f7fa;
}

.small-font-input .el-input__wrapper {
  padding: 0 4px !important;
  height: 28px !important;
  min-height: 28px !important;
}

.small-font-input .el-input__inner {
  font-size: 10px !important;
  font-weight: 400 !important;
  padding: 0 4px !important;
  height: 28px !important;
  line-height: 28px !important;
}

.small-font-input .el-textarea__inner {
  font-size: 10px !important;
  font-weight: 400 !important;
  padding: 4px 6px !important;
  min-height: 40px !important;
  resize: vertical;
}

.el-select .el-input__wrapper {
  padding: 0 4px !important;
  height: 28px !important;
  min-height: 28px !important;
}

.el-select .el-input__inner {
  font-size: 10px !important;
  font-weight: 400 !important;
  padding: 0 4px !important;
  height: 28px !important;
  line-height: 28px !important;
}

.el-date-editor .el-input__wrapper {
  padding: 0 4px !important;
  height: 24px !important;
  min-height: 24px !important;
  border-radius: 4px !important;
}

.el-date-editor .el-input__inner {
  font-size: 10px !important;
  height: 22px !important;
  line-height: 22px !important;
  padding: 0 4px !important;
}

.el-input input[type="number"]::-webkit-inner-spin-button,
.el-input input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}

.el-input input[type="number"] {
  -moz-appearance: textfield !important;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}

.el-table th.el-table__cell .cell {
  white-space: normal;
  word-break: break-word;
  text-align: center;
  font-size: 10px !important;
}

.sorting-field-item .el-input__inner {
  font-size: 11px !important;
  height: 24px !important;
  line-height: 24px !important;
}

.sorting-field-item .el-input__wrapper {
  height: 24px !important;
  min-height: 24px !important;
  padding: 0 4px !important;
}

.dialog-form-container {
  padding: 10px 0;
  overflow-x: auto;
}

.form-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: nowrap;
  min-width: 1100px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  position: relative;
  padding-bottom: 20px;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: #1a2332;
  white-space: nowrap;
}

.form-label.required::after {
  content: ' *';
  color: #e74c3c;
}

.form-input,
.form-select {
  padding: 6px 10px;
  border: 1.5px solid #dce1e8;
  border-radius: 6px;
  font-size: 13px;
  transition: border-color 0.3s;
  background: white;
  width: 100%;
  height: 34px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.form-input.input-error {
  border-color: #e74c3c;
}

.error-text {
  font-size: 10px;
  color: #e74c3c;
  white-space: nowrap;
  position: absolute;
  bottom: 0px;
  left: 0;
  z-index: 10;
}

.success-text {
  color: #67c23a !important;
  font-weight: 600;
}

.checking-text {
  color: #f39c12 !important;
  font-weight: 600;
}

.order-no-wrapper {
  display: flex;
  align-items: center;
  border: 1.5px solid #dce1e8;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.3s;
  height: 34px;
  width: 100%;
}

.order-no-wrapper:focus-within {
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.order-no-prefix {
  background: #f0f4ff;
  padding: 0 4px;
  font-weight: 700;
  color: #1a2332;
  font-size: 10px;
  border-right: 1px solid #dce1e8;
  white-space: nowrap;
  height: 100%;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.order-no-input {
  border: none !important;
  border-radius: 0 !important;
  padding: 0 3px;
  width: 100%;
  text-align: center;
  font-weight: 600;
  font-size: 11px;
  height: 100%;
  min-width: 25px;
}

.order-no-input:focus {
  box-shadow: none !important;
}

.input-success {
  border-color: #67c23a !important;
}

.input-checking {
  border-color: #f39c12 !important;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 1200px) {
  .form-row {
    flex-wrap: wrap;
    min-width: unset;
  }
  .form-group {
    flex: 1;
    min-width: 150px;
  }
}

.el-table__row.status-submitted-row {
  background-color: #fdf6ec !important;
}

.el-table__row.status-submitted-row td {
  background-color: #fdf6ec !important;
}

.el-table .el-button--warning.is-disabled,
.el-table .el-button--warning.is-disabled:hover {
  background-color: #f5dab1 !important;
  border-color: #e6a23c !important;
  color: #fff !important;
}

.el-table .el-button--success.is-disabled,
.el-table .el-button--success.is-disabled:hover {
  background-color: #a8d8b0 !important;
  border-color: #67c23a !important;
  color: #fff !important;
}
</style>